import React from 'react';
import { observer } from 'mobx-react';
import compose from 'helpers/compose';

import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Grid from '@material-ui/core/Grid';
import { Theme } from '@material-ui/core/styles/createTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Breadcrumbs from './Breadcrumbs';
import LogoutDialog from './LogoutDialog';
import { APP_CONFIG } from 'appConfig';
import { RootStore } from 'stores/rootStore';
import SideBar from 'pages/Accounts/Detail/Sidebar/Sidebar';
import { Permission } from 'helpers/permissions';
import { Client } from 'enums/index';

interface Props {
  logoutDialogShown: boolean;
  hideLogoutDialog: () => void;
  rootStore: RootStore;
  detail?: boolean;
  signatureOnly?: boolean;
  noBreadcrumbs?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: (props: Partial<Props>) => ({
      padding: props.detail ? theme.spacing(0) : theme.spacing(2),
    }),
    breadcrumbs: {
      marginBottom: theme.spacing(2),
    },
    versionInfo: {
      padding: '10px',
      color: '#aaa',
      marginTop: '10px',
      textAlign: 'center',
    },
    detailWrapper: {
      display: 'flex',
    },
    sidebar: {
      padding: theme.spacing(0, 0, 0, 0),
      background: theme.palette.common.black,
      zIndex: 1,
      alignItems: 'stretch',
      width: '250px',
      maxWidth: '250px',
      transition: '0.5s all',
      overflow: 'hidden',

      '&:not(.open)': {
        width: '56px',
        maxWidth: '56px',
      },

      [theme.breakpoints.down('sm')]: {
        position: 'absolute',
        minHeight: '100%',
        '&.open': {
          zIndex: 10,
          boxShadow: '0 0 10px 1px rgba(0,0,0,0.13)',
        },
      },
      [theme.breakpoints.down('xs')]: {
        display: 'none',
      },
      '&.signOnly': { display: 'none' },
    },
    detailContent: {
      transition: '0.5s all',
      '&:not(.signOnly)': {
        width: 'calc(100% - 250px)',
        flexBasis: 'calc(100% - 250px)',
        maxWidth: 'calc(100% - 250px)',
      },

      [theme.breakpoints.up('md')]: {
        '&.closed': {
          '&:not(.signOnly)': {
            //marginLeft: '56px',
            width: 'calc(100% - 56px)',
            flexBasis: 'calc(100% - 56px)',
            maxWidth: 'calc(100% - 56px)',
          },
        },
      },
      [theme.breakpoints.down('sm')]: {
        '&:not(.signOnly)': {
          marginLeft: '56px',
          width: 'calc(100% - 56px)',
          flexBasis: 'calc(100% - 56px)',
          maxWidth: 'calc(100% - 56px)',
        },
      },
      [theme.breakpoints.down('xs')]: {
        '&:not(.signOnly)': {
          marginLeft: '0',
          width: '100%',
          flexBasis: '100%',
          maxWidth: '100%',
        },
      },
      '&.signOnly': {
        width: '100% !important',
        flexBasis: '100% !important',
        maxWidth: '100% !important',
      },
    },
    closeMenuOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
  }),
);

const Content: React.FC<Props> = (props) => {
  const { logoutDialogShown, hideLogoutDialog, children } = props;
  const classes = useStyles({ detail: props.detail, signatureOnly: props.signatureOnly });
  const { userStore, uiStore } = props.rootStore.stores;
  const signOnly = userStore.loggedUser?.client === Client.CLIENT_PORTAL_SIGN;
  const tablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'), { noSsr: true });

  return (
    <div className={`${classes.root} ${uiStore.detailMenuOpen ? 'open' : 'closed'} main-scrollable-container`}>
      <LogoutDialog logoutDialogShown={logoutDialogShown} hideLogoutDialog={hideLogoutDialog} />
      {props.detail ? (
        <Grid
          container
          spacing={0}
          alignItems={'stretch'}
          style={{ flexWrap: 'nowrap'}}
          onClick={(e) => {
            if (uiStore.detailMenuOpen && tablet) {
              uiStore.setDetailMenuOpen(false);
              e.stopPropagation();
            }
          }}
        >
          <Grid
            item
            xs={12}
            sm={12}
            md={3}
            lg={3}
            xl={2}
            className={`${classes.sidebar} ${signOnly ? 'signOnly' : ''} ${uiStore.detailMenuOpen ? 'open' : 'closed'}`}
          >
            {uiStore.detailMenuOpen && tablet && <div className={classes.closeMenuOverlay} />}
            <SideBar rootStore={props.rootStore} />
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={9}
            lg={9}
            xl={10}
            className={`${classes.detailContent} ${signOnly ? 'signOnly' : ''} ${
              uiStore.detailMenuOpen ? 'open' : 'closed'
            }`}
          >
            {children}
          </Grid>
        </Grid>
      ) : (
        <>
          {userStore.checkPermissions(Permission.APP_ADVISOR) && !props.noBreadcrumbs && <Breadcrumbs />}
          {children}
        </>
      )}

      {APP_CONFIG.uiShowVersion &&
        userStore.checkPermissions(Permission.APP_ADVISOR) &&
        APP_CONFIG.uiShowVersionClient && <div className={classes.versionInfo}>{uiStore.version}</div>}
    </div>
  );
};

export default compose(observer)(Content);
